import React from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import Text from "../../../atoms/Text";
import Icon from "../../../atoms/Icon";
import Spinner from "../../../atoms/Spinner";
import Button from "../../../atoms/Button";
import { COLOR_SYSTEM } from "../../../design/design-system";
import { SHADOW } from "../../../design/effects";
import { AIReviewStatus, LlmFailureCode } from "../../../../common/models/types";
import { RootState } from "../../../../store";
import { describeLlmFailure } from "./llmFailureCopy";
import { StreamDisconnection } from "../../../../hooks/useAIReviewStream";

const AnalysisStatusContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
`;

const StatusCard = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Blue")[200]};
  border-radius: 12px;
  padding: 20px;
  ${SHADOW.get("1")};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const StatusIconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${COLOR_SYSTEM.get("Blue")[50]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusMessageContainer = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WarningCard = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Orange")[200]};
  border-radius: 12px;
  padding: 20px;
  ${SHADOW.get("1")};
`;

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const WarningIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${COLOR_SYSTEM.get("Orange")[50]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const WarningContent = styled.div`
  margin-left: 44px;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;

  &::before {
    content: "•";
    color: ${COLOR_SYSTEM.get("Orange")[500]};
    margin-right: 8px;
    font-size: 14px;
    line-height: 1.4;
    flex-shrink: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoCard = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Blue")[200]};
  border-radius: 12px;
  padding: 20px;
  ${SHADOW.get("1")};
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${COLOR_SYSTEM.get("Gray")[200]};
  margin: 16px 0;
`;

// 관리자 실패 원문 — 공백 없는 4,000자도 칸 폭 안에서 줄바꿈하고 세로로만 스크롤한다.
const FailureDetail = styled.pre`
  margin: 0;
  padding: 10px;
  background: ${COLOR_SYSTEM.get("Gray")[10]};
  border-radius: 4px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 40vh;
  overflow: auto;
`;

const blink = keyframes`
  0%, 50% { 
    opacity: 1;
  }
  51%, 100% { 
    opacity: 0;
  }
`;

const BlinkingIcon = styled.span`
  display: inline-block;
  animation: ${blink} 2s steps(1) infinite;
  margin-right: 6px;
  font-size: 16px;
`;

interface AnalysisStatusProps {
  status?: AIReviewStatus;
  message?: string;
  /**
   * 백엔드가 분류한 LLM 실패 사유. 값이 있으면 사유별 문구를 그리고 서버 원문(message)은 버린다(결정 6).
   * 관리자 응답의 `llmFailureDetail`만 예외로 그린다(아래 prop, spec 2026-09-14 결정 7).
   */
  llmFailureCode?: LlmFailureCode | null;
  /** 관리자 응답(`analysis.llmFailureDetail`)에만 오는 실패 원문 한 줄. 있을 때만 카드 하단에 블록을 그린다. */
  llmFailureDetail?: string | null;
  /** 원문 기록 시각(ISO 8601). 직전 실패의 원문이 남은 경우를 관리자가 가리도록 캡션에 찍는다(결정 13). */
  llmFailureDetailAt?: string | null;
  /** 스트림 끊김 상태와 [새로고침] 동작 (보고서 화면이 관리하는 로컬 상태). */
  disconnection?: StreamDisconnection;
}

/** 기록 시각을 보는 사람의 로컬 시각(분 단위)으로 표기한다. 값이 깨져 있으면 원문을 그대로 보여 준다. */
const formatRecordedAt = (iso: string): string => {
  const parsed = DateTime.fromISO(iso);
  return parsed.isValid ? parsed.toFormat("yyyy-MM-dd HH:mm") : iso;
};

/**
 * 오류 메시지를 분석하여 토큰 제한 오류인지 판별
 */
const isTokenLimitError = (message?: string): boolean => {
  if (!message) return false;

  // 토큰 제한 관련 키워드
  const tokenLimitKeywords = [
    'token exceed',
    'tokens exceed',
    'token limit',
    'context length',
    'maximum context',
    'input tokens',
    'configured limit'
  ];

  const lowerMessage = message.toLowerCase();
  return tokenLimitKeywords.some(keyword =>
    lowerMessage.includes(keyword)
  );
};

const AnalysisStatus: React.FC<AnalysisStatusProps> = ({
  status,
  message,
  llmFailureCode,
  llmFailureDetail,
  llmFailureDetailAt,
  disconnection,
}) => {
  const isPremiumUser = useSelector((state: RootState) => state.user.isPremiumUser);

  // FAILED 상태 처리
  if (status === AIReviewStatus.FAILED) {
    // 사유 코드가 있으면 확정 문구로 그린다. 코드가 없는 실패(과거 건·조기 종료 건)는
    // 현행 표시 — 영문 키워드 매칭과 message 폴백 — 를 그대로 유지한다(결정 6).
    const copy = llmFailureCode ? describeLlmFailure(llmFailureCode) : null;
    const isLegacyTokenError = !copy && isTokenLimitError(message);
    const title = copy?.title ?? (isLegacyTokenError ? "처방전 분석 범위 초과" : "처리 중 문제가 발생했습니다");
    const body =
      copy?.body ??
      (isLegacyTokenError ? "선택하신 처방전의 내용이 너무 많아 한 번에 분석하기 어렵습니다." : undefined);
    const bullets =
      copy?.bullets ??
      (isLegacyTokenError
        ? ["오래된 처방전은 삭제해주세요.", "중요하지 않은 의약품은 삭제해주세요."]
        : []);

    return (
      <AnalysisStatusContainer>
        {/* 통합된 단일 카드 */}
        <InfoCard>
          <InfoHeader>
            <Icon name="Information Circle" size={24} color={COLOR_SYSTEM.get("Blue")[500]} />
            <Text textType="H4" color={COLOR_SYSTEM.get("Gray")[800]}>
              {title}
            </Text>
          </InfoHeader>

          <InfoContent>
            {body && (
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[700]}>
                {body}
              </Text>
            )}

            {bullets.length > 0 && (
              <BulletList style={{ marginTop: 8, marginBottom: 8 }}>
                {bullets.map((bullet) => (
                  <BulletItem key={bullet}>
                    <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                      {bullet}
                    </Text>
                  </BulletItem>
                ))}
              </BulletList>
            )}

            {/* 포인트 환불 안내 (일반 사용자) / 재시도 안내 (프리미엄 사용자) */}
            {!isPremiumUser ? (
              <>
                <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[700]}>
                  사용하신 포인트는 바로 환불되었습니다.
                </Text>

                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]} style={{ marginTop: 8 }}>
                  분석을 다시 시도해주세요.
                </Text>

                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]} style={{ marginTop: 4 }}>
                  혹시 환불이 확인되지 않으시면 우측 상단의 '문의하기'를 통해 문의해주세요.
                </Text>
              </>
            ) : (
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[700]}>
                다시 시도해주세요.
              </Text>
            )}

            {/* 코드가 없는 옛 실패 건에서만 남는 서버 원문 줄 (코드가 있으면 렌더하지 않는다) */}
            {!copy && message && (
              <>
                <Divider />
                <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")[400]}>
                  오류 상세: {message}
                </Text>
              </>
            )}

            {/* 관리자 응답에만 오는 실패 원문(analysis.llmFailureDetail). 약사·파트너 응답에는 analysis가
                없어 그려지지 않는다. 사유 코드는 라벨 없이 원시 문자열로 둔다(spec 2026-09-14 결정 7). */}
            {llmFailureDetail && (
              <>
                <Divider />
                <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")[500]}>
                  실패 원문 (관리자)
                  {llmFailureDetailAt && ` · 기록 ${formatRecordedAt(llmFailureDetailAt)}`}
                </Text>
                <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")[500]}>
                  {llmFailureCode ?? "(코드 없음)"}
                </Text>
                <FailureDetail>{llmFailureDetail}</FailureDetail>
              </>
            )}
          </InfoContent>
        </InfoCard>
      </AnalysisStatusContainer>
    );
  }

  // 기존 분석 진행 중 상태
  return (
    <AnalysisStatusContainer>
      {/* 분석 상태 카드 — 스트림이 끊기면 무표시로 두지 않고 재확인 수단을 준다 */}
      {disconnection?.isDisconnected ? (
        <StatusCard>
          <StatusIconContainer>
            <Icon name="Exclamation Mark Circle" size={24} color={COLOR_SYSTEM.get("Blue")[500]} />
          </StatusIconContainer>
          <StatusMessageContainer>
            <Text textType="H4" color={COLOR_SYSTEM.get("Gray")[800]}>
              연결이 잠시 끊어졌어요.
            </Text>

            {/* 두 번째 불릿: 버튼이 "새 분석"으로 읽히면 포인트 차감을 걱정하게 된다 — 상태만 다시
                본다는 것을 밝힌다. 프리미엄은 포인트를 쓰지 않으므로 차감 이야기를 빼야 거짓 안내가
                되지 않는다(실패 카드의 환불 안내 분기와 대칭). */}
            <BulletList style={{ textAlign: "left", marginTop: 4 }}>
              <BulletItem>
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                  분석은 계속 진행 중일 수 있으니 잠시 뒤에 아래 버튼을 한번 눌러보세요.
                </Text>
              </BulletItem>
              <BulletItem>
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                  {isPremiumUser
                    ? "버튼을 눌러도 새로 분석하지 않아요."
                    : "버튼을 눌러도 새로 분석하지 않으며, 포인트가 추가로 차감되지 않아요."}
                </Text>
              </BulletItem>
            </BulletList>

            <Button
              size="Medium"
              type="Text"
              variant="Outlined"
              color="Primary"
              disabled={disconnection.isRechecking}
              onClick={disconnection.onRecheck}
              style={{ marginTop: 4 }}
            >
              새로고침
            </Button>
          </StatusMessageContainer>
        </StatusCard>
      ) : (
        <StatusCard>
          <StatusIconContainer>
            <Spinner size="Medium" />
          </StatusIconContainer>
          <StatusMessageContainer>
            <Text textType="H4" color={COLOR_SYSTEM.get("Gray")[800]}>
              AI 분석 진행 중
            </Text>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[600]}>
              {message || "데이터 분석중입니다..."}
            </Text>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Blue")[600]} style={{ marginTop: 8, fontWeight: 500 }}>
              <BlinkingIcon>⚠️</BlinkingIcon>
              10분 이상 기다리실 경우 새로고침을 눌러주세요
            </Text>
          </StatusMessageContainer>
        </StatusCard>
      )}

      {/* AI 주의사항 카드 */}
      <WarningCard>
        <WarningHeader>
          <WarningIconWrapper>
            <Icon
              name="Exclamation Mark Circle"
              size={20}
              color={COLOR_SYSTEM.get("Orange")[500]}
            />
          </WarningIconWrapper>
          <Text textType="H4" color={COLOR_SYSTEM.get("Gray")[800]}>
            AI 기능 사용 시 주의사항
          </Text>
        </WarningHeader>

        <WarningContent>
          <BulletList>
            <BulletItem>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Orange")[600]}>
                AI는 틀린 정보를 제공할 수 있습니다.
              </Text>
            </BulletItem>
            <BulletItem>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                근거 자료를 제시하더라도 AI가 잘못 해석하여 실수할 가능성이 있습니다.
              </Text>
            </BulletItem>
            <BulletItem>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                반드시 근거 자료를 직접 확인하시고, 근거가 명시되지 않은 내용은 약사님의 전문
                지식과 추가 검증을 통해 최종 판단해주세요.
              </Text>
            </BulletItem>
            <BulletItem>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                AI 분석은 일반적 기준에 따른 것으로, 환자의 개별 특성과 상황에 따라 다르게 적용될
                수 있습니다. 환자별 맞춤 상담을 통해 최종 판단해주세요.
              </Text>
            </BulletItem>
            <BulletItem>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                모든 처방 검토 및 상담 결과에 대한 최종 책임은 약사에게 있습니다.
              </Text>
            </BulletItem>
          </BulletList>
        </WarningContent>
      </WarningCard>
    </AnalysisStatusContainer>
  );
};

export default AnalysisStatus;